// WebSocket client for agent metrics (backup/restore progress now uses HTTP polling)

// Agent Metrics WebSocket client
export interface AgentMetricsMessage {
  type: string;
  agent_id: string;
  cpu: number;
  memory: number;
  disk: number;
  io_read: number;
  io_write: number;
  iops: number;
  latency: number;
  timestamp: number;
}

export type MetricsCallback = (message: AgentMetricsMessage) => void;
export type ErrorCallback = (error: string) => void;

export class AgentMetricsClient {
  private ws: WebSocket | null = null;
  private agentId: string;
  private token: string;
  private wsToken: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000;
  private onMetrics: MetricsCallback;
  private onError?: ErrorCallback;
  private isManuallyDisconnecting = false;

  constructor(agentId: string, token: string, onMetrics: MetricsCallback, onError?: ErrorCallback) {
    this.agentId = agentId;
    this.token = token;
    this.onMetrics = onMetrics;
    this.onError = onError;
  }

  private async getWSToken(): Promise<string | null> {
    try {
      const response = await fetch('/api/v1/ws/token', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        console.error('Failed to get WS token:', response.statusText);
        return null;
      }
      const data: { ws_token?: string } = await response.json();
      return data.ws_token || null;
    } catch (error) {
      console.error('Error fetching WS token:', error);
      return null;
    }
  }

  connect(): void {
    this.doConnect().catch((err: unknown) => {
      console.error('WebSocket connection failed:', err);
      this.onError?.('Connection failed');
    });
  }

  private async doConnect(): Promise<void> {
    // Get ws_token first (if not already have one)
    if (!this.wsToken) {
      this.wsToken = await this.getWSToken();
      if (!this.wsToken) {
        this.onError?.('Failed to obtain WebSocket token');
        return;
      }
    }

    // Use relative URL for WebSocket to work with proxy
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/api/v1/ws/agents/${this.agentId}/metrics?ws_token=${this.wsToken}`;

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log(`WebSocket connected for agent ${this.agentId} metrics`);
      this.reconnectAttempts = 0;
    };

    this.ws.onmessage = (event) => {
      try {
        const message: AgentMetricsMessage = JSON.parse(event.data);
        if (message.type === 'metrics') {
          this.onMetrics(message);
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.onError?.('Connection error');
    };

    this.ws.onclose = () => {
      console.log('WebSocket connection closed');

      // Don't auto-reconnect if user manually disconnected
      if (this.isManuallyDisconnecting) {
        this.isManuallyDisconnecting = false;
        return;
      }

      // Attempt to reconnect
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        console.log(`Reconnecting... attempt ${this.reconnectAttempts}`);
        this.wsToken = null; // Clear so we get a fresh one on reconnect
        setTimeout(() => this.connect(), this.reconnectDelay);
      }
    };
  }

  disconnect(): void {
    if (this.ws) {
      this.isManuallyDisconnecting = true;
      this.ws.close();
      this.ws = null;
      this.wsToken = null; // Clear ws_token on disconnect
    }
  }
}
